/* eslint-disable no-shadow */
/**
 *
 * SearchField
 *
 */

import { CircularProgress, TextField } from '@mui/material';
import PropTypes from 'prop-types';
import React, { memo, useEffect, useState } from 'react';
import { withTheme } from 'styled-components';

import {
  CrossCircleIconWrapper,
  LoadingIconContainer,
} from './SearchField.styles';
import CrossCircleIcon from '../../../assets/v2/CrossCircleIcon';
import SearchIcon from '../../../assets/v2/SearchIcon';
import { COLORS } from '../../../theme';

export const ADORNMENT_PLACES = {
  LEFT: 'left',
  RIGHT: 'right',
};

export const SIZES = {
  SMALL: 'small',
  MEDIUM: 'medium',
};

const SearchField = ({
  value,
  onSearch,
  onClear,
  size = SIZES.SMALL,
  placeholder,
  disabled,
  loading,
  adornmentPlace = ADORNMENT_PLACES.LEFT,
  light = true,
  autoFocus,
  inputRef,
  onKeyDown,
  theme,
  customSearchIcon,
  showClearIcon,
  sx,
  type,
  ...props
}) => {
  const [stateValue, setStateValue] = useState(value);
  const isLight = size !== 'medium' && light;

  useEffect(() => {
    setStateValue(value);
  }, [value]);

  const loadingIcon = (
    <LoadingIconContainer>
      <CircularProgress size={16} width={theme.fontSize.body1} />
    </LoadingIconContainer>
  );

  const searchIcon = (
    <SearchIcon
      sx={{
        width: theme.fontSize.body1,
        ...(disabled ? { color: COLORS.black10 } : {}),
      }}
    />
  );

  const clearIcon = (
    <CrossCircleIcon
      sx={{
        width: theme.fontSize.h3,
        color: theme.text,
      }}
    />
  );

  const getStartAdornment = () => {
    if (adornmentPlace === ADORNMENT_PLACES.LEFT) {
      return loading ? loadingIcon : customSearchIcon || searchIcon;
    }
    return null;
  };

  const getEndAdornment = () => {
    if (!value && !showClearIcon) {
      if (adornmentPlace === ADORNMENT_PLACES.RIGHT) {
        // No provided value, and selected place is right
        return searchIcon;
      }
    } else if (value || showClearIcon) {
      // Other cases if value provided
      return (
        <CrossCircleIconWrapper
          onClick={() => {
            setStateValue('');
            if (onClear) {
              onClear();
            }
          }}
        >
          {clearIcon}
        </CrossCircleIconWrapper>
      );
    }
    return null;
  };

  const handleChange = (e) => {
    if (
      type === 'number' &&
      (!e?.nativeEvent?.inputType ||
        ['e', 'E', '+', '-'].includes(e?.nativeEvent?.data))
    ) {
      return;
    }
    setStateValue(e.target.value);

    if (onSearch) {
      onSearch(e);
    }
  };

  return (
    <TextField
      {...props}
      value={stateValue}
      onChange={handleChange}
      autoFocus={autoFocus}
      variant="outlined"
      size={size}
      type={type}
      InputProps={{
        startAdornment: getStartAdornment(),
        endAdornment: getEndAdornment(),
        ...props?.InputProps,
      }}
      placeholder={placeholder}
      disabled={disabled}
      onKeyDown={onKeyDown}
      inputRef={inputRef}
      sx={{
        width: '100%',
        background: COLORS.white,
        borderRadius: theme.borderRadius.secondary,
        '.MuiOutlinedInput-root': {
          padding: `0 ${theme.spacing.s2}`,
          '&.Mui-focused input + span': {
            opacity: 1,
            cursor: 'pointer',
          },
          '&.Mui-disabled span': {
            opacity: '0.3',
          },
        },
        '.MuiInputBase-adornedStart': {
          height: theme.spacing.l4,
          '&.MuiInputBase-sizeSmall': {
            height: theme.spacing.l2,
          },
          svg: {
            marginRight: theme.spacing.s2,
          },
          input: {
            fontSize: isLight ? theme.fontSize.body2 : theme.fontSize.body1,
            fontWeight: isLight ? 400 : 500,
            '+ span': {
              opacity: '0.4',
              marginRight: 0,
            },
          },
        },
        '.MuiFormHelperText-root': {
          // Designs said, that should be right, but in views its stick to left, changed here
          textAlign: 'left',
          margin: '2px 14px',
        },
        ...sx,
      }}
    />
  );
};

SearchField.propTypes = {
  value: PropTypes.string,
  onSearch: PropTypes.func,
  onClear: PropTypes.func,
  size: PropTypes.oneOf(Object.values(SIZES)),
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  adornmentPlace: PropTypes.oneOf(Object.values(ADORNMENT_PLACES)),
  autoFocus: PropTypes.bool,
  inputRef: PropTypes.any,
  onKeyDown: PropTypes.func,
  light: PropTypes.bool,
  theme: PropTypes.object,
  InputProps: PropTypes.object,
  customSearchIcon: PropTypes.node,
  sx: PropTypes.object,
  showClearIcon: PropTypes.bool,
  type: PropTypes.string,
};

export default memo(withTheme(SearchField));
